import CASBlock from "@components/Blocks/CASBlock/CASBlock";
import TranslationPopover from "@components/Modules/Translation/TranslationPopover";
import WordPuzzle from "@components/Modules/WordPuzzle/WordPuzzle";
import { pubPageAdTargeting } from "@config/ads";
import { makeSvgDataSrc } from "@elements/Advertisement/helper/makeSvgData";
import Taboola from "@elements/Advertisement/Taboola";
import { getTagsName } from "@elements/GAData/GADataHelper";
import {
  CAS_UGC_SECTION_BANNER_ID,
  IS_PLAYWRIGHT_TEST,
} from "@helper/getEnvVariables";
import { useBHVirtualPVAnalyticsCall } from "@hooks/useAnalyticsCall/useBHVirtualPVAnalyticsCall";
import useShortFormVideo from "@hooks/useBrightcoveVideo/useShortFormVideo";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import BHArticleBasePage, {
  AdsSettings,
  BHArticleBasePageProps,
} from "@pages/BHArticleBasePage";
import ErrorPage from "@pages/ErrorPage";
import NotFound from "@pages/NotFound";
import {
  CustomContext,
  TRouteWithRedirect,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import { useEffect, useRef, useState } from "react";

import { PayloadResponseType } from "./Article.server";
import ArticleInfiniteScroll from "./ArticleInfiniteScroll";
import ArticleContentCardNew from "./ArticleLayout/ArticleContentCardNew";
import ArticleMetaData from "./ArticleMetaData";
import ArticleElementWrapper from "./Components/ArticleElementWrapper";
import ShortFormVideos from "./Components/ShortFormVideos";

export function ArticlePage() {
  const routeContext: CustomContext<
    TRouteWithRedirect<PayloadResponseType, string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;
  const [currentArticle, setCurrentArticle] = useState<ProcessedArticleData>();
  const [pagination, setPagination] = useState<number>(0);
  const [resolvedGsChannels, setResolvedGsChannels] = useState<string[]>([]);
  const { gsChannels, isLoading } = useGrapeshotData(
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? (dataLoaderResponse.payload.article.urlPath as string)
      : "",
  );
  const analyticsCall = useBHVirtualPVAnalyticsCall();
  const containerRef = useRef<HTMLDivElement>(null);
  const { playing, videos, handleOnClickThumbnail, handleOnEndedVideo } =
    useShortFormVideo({ containerRef });
  const containerRefLong = useRef<HTMLDivElement>(null);

  // update when gsChannels are loaded
  useEffect(() => {
    if (!isLoading && gsChannels.length) {
      setResolvedGsChannels(gsChannels);
    }
  }, [isLoading, gsChannels]);

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;
    const PubPageAdTarget = pubPageAdTargeting[
      pageAdTargetValue
    ] as AdTargetingType<ArticleAdTargetKeys, string>;
    const mainArticle = dataLoaderResponse.payload.article;

    const handleNewArticle = (
      newArticle: ProcessedArticleData,
      pageNumber: number,
      viewPortChange: boolean,
    ) => {
      setCurrentArticle(newArticle);
      setPagination(pageNumber);
      if (viewPortChange) {
        setTimeout(() => {
          analyticsCall(
            newArticle.title,
            undefined,
            undefined,
            undefined,
            pageNumber,
          );
        }, 300);
      }
    };

    const { gaData, metaTags } = ArticleMetaData({
      article: currentArticle || mainArticle,
      pageNumber: pagination,
      gsChannels:
        currentArticle && currentArticle.gsChannels
          ? currentArticle.gsChannels
          : resolvedGsChannels,
    });

    const bhTags = getTagsName(mainArticle.tags || []);
    const bhArticleId = mainArticle.id;
    const articleSlotTarget: AdTargetingType<ArticleAdTargetKeys, string>[] = [
      { key: ArticleAdTargetKeys.ARTICLE_ID, value: bhArticleId },
      { key: ArticleAdTargetKeys.TAGS, value: bhTags.toLowerCase() },
      PubPageAdTarget,
    ];

    const adsUniqueSectionName = mainArticle.adsUniqueSectionName;

    const basePageProps: BHArticleBasePageProps = {
      adsUniqueSectionName,
      gaData,
      metaTags,
      gsChannels: resolvedGsChannels,
      articleSlotTarget,
      prestitialPageTargeting: pageAdTargetValue,
      articleId: currentArticle ? currentArticle.id : mainArticle.id,
    };

    const rednerArticlePage = (isTaboolaEnabled: boolean) => {
      return (
        <>
          {adsUniqueSectionName == "mata-mata" && (
            <div className="bg-white-200 flex flex-col xl:flex-row items-center justify-center mb-5 py-md min-h-[200px] lg:min-h-[220px]">
              {IS_PLAYWRIGHT_TEST === "true" ? (
                <div>
                  <img
                    className="max-w-full"
                    src={makeSvgDataSrc({
                      width: 860,
                      height: 200,
                      text: "CAS Banner",
                    })}
                    alt="cas-banner"
                  />
                </div>
              ) : (
                <CASBlock casId={CAS_UGC_SECTION_BANNER_ID} />
              )}
            </div>
          )}
          <div id={mainArticle.id} className="article-card mt-5">
            <ArticleContentCardNew
              mainArticle={mainArticle}
              pageNumber={0}
              isInfiniteScrollArticle={false}
              gsChannels={resolvedGsChannels}
              articleSlotTarget={articleSlotTarget}
              visibleArticleId={
                currentArticle ? currentArticle.id : mainArticle.id
              }
            />
            <ArticleElementWrapper>
              <ShortFormVideos
                containerRef={containerRef}
                videos={videos}
                handleOnClickThumbnail={handleOnClickThumbnail}
                handleOnEndedVideo={handleOnEndedVideo}
                playing={playing}
              />
              <div className="mt-4 lg:mt-6">
                {isTaboolaEnabled && mainArticle.urlPath && (
                  <Taboola
                    counter={0}
                    pathName={mainArticle.urlPath}
                    pageType="article"
                    mode="thumbnails-base-widget"
                    container="below-article-thumbnails"
                    placement="Below Article Thumbnails"
                    targetType="mix"
                  />
                )}
              </div>
            </ArticleElementWrapper>
          </div>
        </>
      );
    };

    return (
      <>
        <BHArticleBasePage {...basePageProps}>
          {(adsSettings: AdsSettings) => (
            <>
              {rednerArticlePage(adsSettings.isTaboolaEnabled || false)}
              <ArticleInfiniteScroll
                mainArticle={mainArticle}
                onNewArticle={handleNewArticle}
                isTaboolaEnabled={adsSettings.isTaboolaEnabled || false}
                PubPageAdTarget={PubPageAdTarget}
                containerRef={containerRef}
                videos={videos}
                handleOnClickThumbnail={handleOnClickThumbnail}
                handleOnEndedVideo={handleOnEndedVideo}
                playing={playing}
                containerRefLong={containerRefLong}
              />
              <TranslationPopover
                gsChannels={resolvedGsChannels}
                articleSlotTarget={articleSlotTarget}
              />
              <WordPuzzle isCatfishEnabled={adsSettings.isCatfishEnabled} />
            </>
          )}
        </BHArticleBasePage>
      </>
    );
  }
}
